import { useState, useCallback } from 'react';
import { LabelElement } from '../types';

interface HistoryState {
  past: LabelElement[][];
  present: LabelElement[];
  future: LabelElement[][];
}

export const useHistory = (initialPresent: LabelElement[]) => {
  const [state, setState] = useState<HistoryState>({
    past: [],
    present: initialPresent,
    future: [],
  });

  const canUndo = state.past.length > 0;
  const canRedo = state.future.length > 0;

  const recordChange = useCallback((newPresent: LabelElement[]) => {
    setState((prevState) => ({
      past: [...prevState.past, prevState.present],
      present: newPresent,
      future: [],
    }));
  }, []);

  // Update elements without recording history (for preview mode operations)
  const updateElementsWithoutHistory = useCallback(
    (newPresent: LabelElement[]) => {
      setState((prevState) => ({
        ...prevState,
        present: newPresent,
      }));
    },
    []
  );

  // Reset history while keeping current elements (for mode switches)
  const resetHistory = useCallback(() => {
    setState((prevState) => ({
      past: [],
      present: prevState.present,
      future: [],
    }));
  }, []);

  const undo = useCallback(() => {
    setState((prevState) => {
      if (prevState.past.length === 0) return prevState;

      const previous = prevState.past[prevState.past.length - 1];
      const newPast = prevState.past.slice(0, -1);

      return {
        past: newPast,
        present: previous,
        future: [prevState.present, ...prevState.future],
      };
    });
  }, []);

  const redo = useCallback(() => {
    setState((prevState) => {
      if (prevState.future.length === 0) return prevState;

      const next = prevState.future[0];
      const newFuture = prevState.future.slice(1);

      return {
        past: [...prevState.past, prevState.present],
        present: next,
        future: newFuture,
      };
    });
  }, []);

  return {
    elements: state.present,
    recordChange,
    updateElementsWithoutHistory,
    resetHistory,
    undo,
    redo,
    canUndo,
    canRedo,
  };
};
